import React from "react";

export type TimeRange = 'monthly' | 'quarterly' | 'yearly' | 'all';

interface ChartFilterProps {
  active: TimeRange;
  onChange: (val: TimeRange) => void;
  isSubscriptionOnly?: boolean;
  subscriptionStartDate?: string | Date | null;
  supportsAllTime?: boolean;
  selectedYear?: number | null;
}

/** Days elapsed since subscription started (returns Infinity if no date supplied) */
const daysElapsed = (startDate?: string | Date | null): number => {
  if (!startDate) return Infinity;
  const start = new Date(startDate);
  return Math.floor((Date.now() - start.getTime()) / (1000 * 60 * 60 * 24));
};

/** Thresholds: once enough data exists we switch from "First X" → "Last X" labels */
const HAS_MONTHLY  = 30;   // 30 days for monthly
const HAS_QUARTERLY = 90;  // 90 days for quarterly
const HAS_YEARLY   = 365;  // 365 days for yearly

/** Returns the short button label shown on the toggle buttons */
export const getFilterButtonLabel = (
  option: TimeRange,
  isSubscriptionOnly: boolean,
  subscriptionStartDate?: string | Date | null,
  selectedYear?: number | null
): string => {
  if (isSubscriptionOnly) {
    const elapsed = daysElapsed(subscriptionStartDate);
    switch (option) {
      case 'monthly':   return elapsed >= HAS_MONTHLY   ? 'Last 30D'  : 'First Month';
      case 'quarterly': return elapsed >= HAS_QUARTERLY ? 'Last 90D'  : 'First 3 Mo';
      case 'yearly':    return elapsed >= HAS_YEARLY    ? 'Last 365D' : 'Subscription';
      case 'all':       return elapsed >= HAS_YEARLY    ? 'Last 365D' : 'Subscription';
    }
  }
  switch (option) {
    case 'monthly':   return 'Last 30D';
    case 'quarterly': return 'Last 90D';
    case 'yearly':    return (selectedYear && selectedYear === new Date().getFullYear()) ? selectedYear.toString() : 'Last 365D';
    case 'all':       return 'All Time';
  }
};

/** Returns the full human-readable label used in chart titles */
export const getPeriodLabel = (
  option: TimeRange,
  isSubscriptionOnly: boolean,
  subscriptionStartDate?: string | Date | null,
  selectedYear?: number | null
): string => {
  if (isSubscriptionOnly) {
    const elapsed = daysElapsed(subscriptionStartDate);
    switch (option) {
      case 'monthly':   return elapsed >= HAS_MONTHLY   ? 'Last 30 Days'   : 'First Month';
      case 'quarterly': return elapsed >= HAS_QUARTERLY ? 'Last 90 Days'   : 'First 3 Months';
      case 'yearly':    return elapsed >= HAS_YEARLY    ? 'Last 12 Months' : 'Full Subscription';
      case 'all':       return elapsed >= HAS_YEARLY    ? 'Last 12 Months' : 'Full Subscription';
    }
  }
  switch (option) {
    case 'monthly':   return 'Last 30 Days';
    case 'quarterly': return 'Last 90 Days';
    case 'yearly':    return (selectedYear && selectedYear === new Date().getFullYear()) ? `Year ${selectedYear}` : 'Last 12 Months';
    case 'all':       return 'All Time';
  }
};

const ChartFilter: React.FC<ChartFilterProps> = ({
  active,
  onChange,
  isSubscriptionOnly = false,
  subscriptionStartDate,
  supportsAllTime = false,
  selectedYear = null
}) => {
  const showAllTime = supportsAllTime && !isSubscriptionOnly;

  return (
    <div className="flex bg-slate-50 p-1 rounded-lg border border-slate-200 select-none gap-0.5">
      {/* Monthly Button */}
      <button
        onClick={() => onChange('monthly')}
        className={`px-3 py-1 text-[10px] font-medium rounded-md transition-all ${
          active === 'monthly'
            ? 'bg-[#010D2C] text-white shadow-sm'
            : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200/50'
        }`}
      >
        {getFilterButtonLabel('monthly', isSubscriptionOnly, subscriptionStartDate, selectedYear)}
      </button>

      {/* Quarterly Button */}
      <button
        onClick={() => onChange('quarterly')}
        className={`px-3 py-1 text-[10px] font-medium rounded-md transition-all ${
          active === 'quarterly'
            ? 'bg-[#010D2C] text-white shadow-sm'
            : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200/50'
        }`}
      >
        {getFilterButtonLabel('quarterly', isSubscriptionOnly, subscriptionStartDate, selectedYear)}
      </button>

      {/* Yearly / Last 365D Button */}
      <button
        onClick={() => onChange('yearly')}
        className={`px-3 py-1 text-[10px] font-medium rounded-md transition-all ${
          active === 'yearly'
            ? 'bg-[#010D2C] text-white shadow-sm'
            : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200/50'
        }`}
      >
        {getFilterButtonLabel('yearly', isSubscriptionOnly, subscriptionStartDate, selectedYear)}
      </button>

      {/* All Time Button */}
      {showAllTime && (
        <button
          onClick={() => onChange('all')}
          className={`px-3 py-1 text-[10px] font-medium rounded-md transition-all ${
            active === 'all'
              ? 'bg-[#010D2C] text-white shadow-sm'
              : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200/50'
          }`}
        >
          {getFilterButtonLabel('all', isSubscriptionOnly, subscriptionStartDate, selectedYear)}
        </button>
      )}
    </div>
  );
};

export default ChartFilter;
